import { formatDistanceToNow, parseISO } from "date-fns";
import { memoize } from "lodash";

import {
  EXPERTS_LAUNCH_SEGMENT,
  LIKE_LISTINGS_SEGMENT,
  LUXURY_SEGMENT,
  TOWNHOUSE_SEGMENT,
  VERTICAL_LIVING_SEGMENT
} from "src/shared/experts/constants/segments";
import { formatMoney } from "src/shared/utilities/formatMoney";
import { formatNumber } from "src/shared/utilities/formatNumber";

import { switchWithSegmentName } from "../segments";

export default class Deal {
  static SALE = "Sale";
  static RENTAL = "Rental";
  static CLOSING = "Closing";

  static VALID_DEAL_TYPES = [Deal.SALE, Deal.RENTAL, Deal.CLOSING];

  getPrice = memoize(() => {
    const { price } = this._data;

    return price ? formatMoney(price) : null;
  });

  getInformation = memoize(() => {
    const dealInformation = [
      this._data.bedrooms,
      this._data.bathrooms,
      this._data.size_sqft > 0
        ? `${formatNumber(this._data.size_sqft)} sqft`
        : null
    ].filter(Boolean);

    return dealInformation.length ? dealInformation : null;
  });

  // eslint-disable-next-line consistent-return
  getStatus = memoize(() => {
    // eslint-disable-next-line default-case
    switch (this.listingType) {
      case Deal.CLOSING:
        return this._getClosingStatus();
      case Deal.SALE:
        return this._getSaleStatus();
      case Deal.RENTAL:
        return this._getRentalStatus();
    }
  });

  _dateFormatter = date =>
    formatDistanceToNow(parseISO(date), {
      addSuffix: true
    }).replace(/^(about\s?)?/, this._getDateFormatStart());

  constructor(data) {
    this._data = Object.freeze(data);

    Object.freeze(this);
  }

  get listingType() {
    return this._data.listing_type;
  }

  get listingUrl() {
    return this._data.listing_url;
  }

  get id() {
    return this._data.id;
  }

  getAddress() {
    const { address, unit } = this._data;

    return switchWithSegmentName({
      [EXPERTS_LAUNCH_SEGMENT]: `${address} ${unit}`,
      [LIKE_LISTINGS_SEGMENT]: `${address} ${unit}`,
      [LUXURY_SEGMENT]: `${address} ${unit}`,
      [TOWNHOUSE_SEGMENT]: address,
      [VERTICAL_LIVING_SEGMENT]: unit
    });
  }

  // eslint-disable-next-line consistent-return
  _getDateFormatStart() {
    // eslint-disable-next-line default-case
    switch (this.listingType) {
      case Deal.SALE:
      case Deal.CLOSING:
        return "closed ";
      case Deal.RENTAL:
        return "rented ";
    }
  }

  _getClosingStatus() {
    const { closed_at } = this._data;

    if (closed_at) {
      return this._dateFormatter(closed_at);
    }

    return null;
  }

  _getSaleStatus() {
    const { closed_at, listing_status } = this._data;

    if (listing_status === "open" || listing_status === "pending") {
      return "On Market";
    } else if (closed_at) {
      return this._dateFormatter(closed_at);
    }

    return "Not yet recorded";
  }

  _getRentalStatus() {
    const { closed_at } = this._data;

    if (closed_at) {
      return this._dateFormatter(closed_at);
    }

    return "On Market";
  }
}
